/** Cycle-level runs projected into a selected-ballot ledger; no DOM or reads. */
import type {
  PublicCycle,
  PublicCycleRace,
  PublicCycleCandidacy,
  PublicCycleCandidacyBallot,
  PublicRace,
  PublicRaceCandidate,
} from "./api";
import { candidacyStatusValue } from "./labels";
export interface StageTag {
  status: "won" | "lost" | "advanced" | "withdrawn";
  text: string;
}
export interface BallotCandidate extends PublicRaceCandidate {
  key: string;
  stage: string;
  tags: StageTag[];
  endorsementBallotLabel: string | null;
  historicalEndorsed: boolean;
  showStatusBadge: boolean;
}
export interface FoldedGroup {
  ballot: string;
  label: string;
  candidates: BallotCandidate[];
  endorsedCount: number;
}
export interface SelectedCycleRace extends PublicRace {
  candidates: BallotCandidate[];
  folds: FoldedGroup[];
  eligible: boolean;
  all: boolean;
}
const SHORT_DATE = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});
export const shortBallotDate = (date: string) =>
  SHORT_DATE.format(new Date(`${date}T12:00:00Z`));
export function selectBallotPresentation(
  cycle: PublicCycle,
  selected: string,
): SelectedCycleRace[] {
  const ballots = [...cycle.ballots].sort((a, b) =>
    a.date.localeCompare(b.date),
  );
  const indices = new Map(ballots.map((b, i) => [b.slug, i]));
  const bySlug = new Map(ballots.map((b) => [b.slug, b]));
  const all = selected === "all";
  const multiple = ballots.length > 1;
  function last(run: PublicCycleCandidacy) {
    return [...run.ballots]
      .sort(
        (a, b) => (indices.get(a.ballot) ?? -1) - (indices.get(b.ballot) ?? -1),
      )
      .at(-1)!;
  }
  function tags(
    run: PublicCycleCandidacy,
    appearances: PublicCycleCandidacyBallot[],
  ): StageTag[] {
    if (!multiple) return [];
    return appearances.flatMap((a) => {
      const ballot = bySlug.get(a.ballot);
      // Winning a primary moves a candidate on; only a later ballot's win decides the
      // Race, so that alone is tagged "won" (and drawn as the filled result).
      const raw = candidacyStatusValue(a.status);
      const status = raw === "won" && ballot?.stage === "primary" ? "advanced" : raw;
      if (
        !ballot ||
        !["won", "lost", "advanced", "withdrawn"].includes(status ?? "")
      )
        return [];
      const next = run.ballots.find(
        (b) => (indices.get(b.ballot) ?? -1) > (indices.get(a.ballot) ?? -1),
      );
      const nextStage = next ? bySlug.get(next.ballot)?.stage : null;
      const verb =
        status === "advanced"
          ? ballot.stage === "primary"
            ? "Won primary"
            : nextStage === "runoff"
              ? "Advanced to runoff"
              : "Advanced"
          : status === "withdrawn"
            ? "Withdrew"
            : status === "lost" && ballot.stage === "primary"
              ? "Lost primary"
              : `${status === "won" ? "Won" : "Lost"} ${ballot.label.toLowerCase()}`;
      return [
        {
          status: status as StageTag["status"],
          text: verb,
        },
      ];
    });
  }
  function row(
    run: PublicCycleCandidacy,
    appearance: PublicCycleCandidacyBallot,
    show: PublicCycleCandidacyBallot[],
  ): BallotCandidate {
    const made = appearance.madeInBallot ?? run.madeInBallot;
    const revoked =
      appearance.revokedInBallot ??
      (appearance.endorsement === "revoked" ? appearance.ballot : null);
    return {
      key: run.key,
      stage: bySlug.get(appearance.ballot)?.stage ?? "",
      name: run.name,
      publicUrl: run.publicUrl,
      party: run.party,
      isIncumbent: run.isIncumbent,
      responseStatus: run.responseStatus,
      status: appearance.status,
      endorsement: {
        endorsed: appearance.endorsement === "endorsed",
        madeInBallot: made,
        revokedInBallot: revoked,
      },
      tags: tags(run, show),
      showStatusBadge:
        !multiple ||
        !show.some(
          (b) =>
            b.ballot === appearance.ballot &&
            ["won", "lost", "advanced", "withdrawn"].includes(
              candidacyStatusValue(b.status) ?? "",
            ),
        ),
      historicalEndorsed: run.ballots.some((b) => b.endorsement === "endorsed"),
      // Name the date only when the standing changed between this run's
      // Election dates; a uniform endorsement is just "Endorsed".
      endorsementBallotLabel: new Set(
        run.ballots.filter((b) => bySlug.has(b.ballot)).map((b) => b.endorsement),
      ).size < 2
        ? null
        : bySlug.get(
          appearance.endorsement === "revoked"
            ? (revoked ?? appearance.ballot)
            : (made ?? ""),
        )?.label ?? null,
    };
  }
  function present(race: PublicCycleRace): SelectedCycleRace {
    const runs = race.candidacies.filter((c) =>
      c.ballots.some((b) => bySlug.has(b.ballot)),
    );
    const leading = runs.filter(
      (c) => all || c.ballots.some((b) => b.ballot === selected),
    );
    // Backbone sends runs in Candidate order; a stable sort keeps it within a ballot.
    if (all)
      leading.sort(
        (a, b) =>
          (indices.get(last(b).ballot) ?? -1) -
          (indices.get(last(a).ballot) ?? -1),
      );
    const candidates = leading.map((c) => {
      const a = all ? last(c) : c.ballots.find((b) => b.ballot === selected)!;
      return row(
        c,
        a,
        all ? c.ballots : c.ballots.filter((b) => b.ballot !== selected),
      );
    });
    const grouped = new Map<string, PublicCycleCandidacy[]>();
    if (!all && multiple)
      for (const run of runs.filter((c) => !leading.includes(c))) {
        const ballot = last(run).ballot;
        grouped.set(ballot, [...(grouped.get(ballot) ?? []), run]);
      }
    const folds = [...grouped]
      .sort(([a], [b]) => (indices.get(b) ?? -1) - (indices.get(a) ?? -1))
      .map(([slug, list]) => {
        const ballot = bySlug.get(slug)!;
        const later = (indices.get(slug) ?? -1) > (indices.get(selected) ?? -1);
        return {
          ballot: slug,
          label: later
            ? `On the ${shortBallotDate(ballot.date)} ballot`
            : `Also ran in the ${shortBallotDate(ballot.date)} ${ballot.label.toLowerCase()}`,
          endorsedCount: list.filter(
            (c) =>
              last(c).endorsement === "endorsed" &&
              last(c).madeInBallot === slug,
          ).length,
          candidates: list.map((c) => row(c, last(c), [last(c)])),
        };
      });
    return {
      slug: race.slug,
      name: race.name,
      seatsToFill: race.seatsToFill,
      body: race.body,
      currentHolders: race.currentHolders,
      advancesToBallot: null,
      questionnaireNotice: race.questionnaireNotice ?? null,
      candidates,
      folds,
      all,
      eligible:
        candidates.length > 0 &&
        (all ||
          candidates.some(
            (c) => candidacyStatusValue(c.status) !== "withdrawn",
          )),
    };
  }
  return cycle.races.map(present);
}
